/**
 * AdminRecordModal - Standardized modal shell for Admin record dialogs
 * 
 * Enforces consistent:
 * - Width (720px default, 900px large)
 * - Padding (24px)
 * - Scrollable body with max-height
 * - Header/footer stability
 * - Footer button placement (Cancel left, Save right)
 * 
 * Usage:
 * <AdminRecordModal
 *   open={isOpen}
 *   onOpenChange={setIsOpen}
 *   title="Edit Campaign"
 *   description="Update campaign settings"
 *   size="lg"
 *   footer={<>...</>}
 * >
 *   <form content>
 * </AdminRecordModal>
 */

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";

const sizeClasses = {
  sm: "sm:max-w-md",      // 448px
  md: "sm:max-w-[720px]", // 720px - default
  lg: "sm:max-w-[900px]", // 900px - complex forms
  xl: "sm:max-w-[1024px]", // 1024px - very wide
};

export function AdminRecordModal({
  open,
  onOpenChange,
  title,
  description,
  size = "md",
  twoColumn = false,
  children,
  footer,
  className = "",
  "data-testid": testId,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={`
          bg-zinc-900 border-zinc-700 text-white
          w-[95vw] ${sizeClasses[size] || sizeClasses.md}
          p-0 gap-0 overflow-hidden
          ${className}
        `}
        data-testid={testId}
      >
        {/* Fixed Header */}
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-zinc-800 flex-shrink-0">
          <DialogTitle className="text-lg font-semibold text-white">
            {title}
          </DialogTitle>
          {description && (
            <DialogDescription className="text-sm text-zinc-400 mt-1">
              {description}
            </DialogDescription>
          )}
        </DialogHeader>

        {/* Scrollable Body */}
        <div 
          className={`
            px-6 py-5 overflow-y-auto
            max-h-[calc(75vh-140px)]
            ${twoColumn ? "grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5" : "space-y-5"}
          `}
        >
          {children}
        </div>

        {/* Fixed Footer */}
        {footer && (
          <div className="px-6 py-4 border-t border-zinc-800 flex-shrink-0 bg-zinc-900/80">
            <div className="flex justify-between items-center gap-3">
              {footer}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/**
 * Helper component for modal footer with standard button layout
 * Cancel on left, primary action(s) on right
 */
export function AdminModalFooter({ 
  onCancel, 
  cancelLabel = "Cancel",
  children // Primary buttons go here
}) {
  return (
    <>
      <button
        type="button"
        onClick={onCancel}
        className="px-4 py-2 text-sm font-medium rounded-md border border-zinc-600 text-zinc-300 hover:bg-zinc-800 transition-colors"
      >
        {cancelLabel}
      </button>
      <div className="flex items-center gap-2">
        {children}
      </div>
    </>
  );
}

export default AdminRecordModal;
